import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, SimpleChanges, OnChanges, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, of, takeUntil } from 'rxjs';
import { SubjectSectionSearchDialogComponent } from '../../subject-section/subject-section-search-dialog/subject-section-search-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
  selector: 'app-subject-extended-search',
  templateUrl: './subject-extended-search.component.html',
  styleUrls: ['./subject-extended-search.component.scss']
})
export class SubjectExtendedSearchComponent implements OnInit, OnDestroy, OnChanges {

  @Input() service: any;
  @Input() edit: boolean = false;
  @Input() searchCleaner: boolean = false;
  @Input() showAdvancedSearch: boolean = false;
  @Output() selected = new EventEmitter<any>();
  @Output() clearOut = new EventEmitter<any>();
  @Output() resultsLoaded = new EventEmitter<any[]>();

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

  myControl = new UntypedFormControl('');
  optionList: any[] = [];
  isLoading = false;
  noResults = false;
  lastDialogFilters: any = null;

  private destroy$ = new Subject<void>();

  constructor(public translate: TranslateService, private dialog: MatDialog) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['searchCleaner'] && this.searchCleaner === true) {
      this.clear();
    }
  }

  ngOnInit(): void {
    const search$: Observable<any> = this.myControl.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((val: any) => {
        if (!val || typeof val !== 'string' || val.trim().length < 2) {
          this.optionList = [];
          this.noResults = false;
          return of([]);
        }
        this.isLoading = true;
        return this.service.extendedSearch({ text: val.trim() }) as Observable<any>;
      }),
      takeUntil(this.destroy$)
    );

    search$.subscribe({
      next: (results: any[]) => {
        this.isLoading = false;
        this.optionList = results || [];
        this.noResults = this.optionList.length === 0;
        this.resultsLoaded.emit(this.optionList);
      },
      error: () => {
        this.isLoading = false;
        this.optionList = [];
        this.noResults = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get activeFiltersCount(): number {
    if (!this.lastDialogFilters) return 0;
    const { rawSelections, ...countableFilters } = this.lastDialogFilters as any;
    return Object.values(countableFilters).filter(
      v => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && (v as any[]).length === 0)
    ).length;
  }

  openAdvancedSearchDialog(): void {
    const ref = this.dialog.open(SubjectSectionSearchDialogComponent, {
      width: '480px',
      data: this.lastDialogFilters ?? {},
      scrollStrategy: new NoopScrollStrategy(),
    });
    ref.afterClosed().subscribe((filters: any) => {
      if (filters === null || filters === undefined) return;
      this.lastDialogFilters = filters;
      this.myControl.setValue('', { emitEvent: false });
      this.isLoading = true;
      const { rawSelections, companyId, assignmentOperator, ...rest } = filters;
      const serverFilters = {
        ...rest,
        ...(companyId?.length ? { company: companyId.map((c: any) => c.code) } : {}),
        ...(assignmentOperator?.length ? { assignmentOperator: assignmentOperator.map((r: any) => r.email) } : {})
      };
      this.service.extendedSearch(serverFilters).subscribe({
        next: (results: any[]) => {
          this.isLoading = false;
          this.optionList = results || [];
          this.noResults = this.optionList.length === 0;
          this.resultsLoaded.emit(this.optionList);
          setTimeout(() => this.autocompleteTrigger?.openPanel());
        },
        error: () => {
          this.isLoading = false;
          this.optionList = [];
          this.noResults = true;
        }
      });
    });
  }

  resetDialogFilters(): void {
    this.lastDialogFilters = null;
    this.clear();
  }

  onSelectionChanged(event: MatAutocompleteSelectedEvent): void {
    this.selected.emit(event.option.value);
  }

  displayFn(subject: any): string {
    if (!subject || typeof subject !== 'object') return subject ?? '';
    const parts = [subject.name, subject.surname].filter(Boolean);
    return parts.join(' ');
  }

  clear(): void {
    this.myControl.setValue('');
    this.optionList = [];
    this.noResults = false;
    this.lastDialogFilters = null;
    this.clearOut.emit();
  }
}
